/**
 * Utility functions for managing website colors
 */

/**
 * Generate CSS custom properties from color settings
 * @param {Object} colors - Color settings object
 * @returns {string} CSS string with custom properties
 */
export const generateColorCSS = (colors) => {
    const cssVariables = Object.entries(colors)
        .map(([key, value]) => `    --${key.replace(/_/g, '-')}: ${value};`)
        .join('\n');

    // Add legacy variables for backward compatibility
    const legacyVariables = `
    --themecolor: var(--primary-color);
    --secthemecolor: var(--secondary-color);`;

    return `:root {\n${cssVariables}${legacyVariables}\n}`;
};

/**
 * Generate complete CSS file content with color variables
 * @param {Object} colors - Color settings object
 * @returns {string} Complete CSS file content
 */
export const generateCompleteCSS = (colors) => {
    const baseCSS = `/* Dynamic Color Variables - Generated from Admin Panel */
/* This file is auto-generated from the admin color settings */
:root {
${Object.entries(colors)
    .map(([key, value]) => `    --${key.replace(/_/g, '-')}: ${value};`)
    .join('\n')}
    
    /* Legacy Variables for Backward Compatibility */
    --themecolor: var(--primary-color);
    --secthemecolor: var(--secondary-color);
}

/* Apply colors to common elements */
body {
    background-color: var(--background-color);
    color: var(--text-color);
}

/* Headers */
h1, h2, h3, h4, h5, h6 {
    color: var(--text-color);
}

/* Links */
a {
    color: var(--link-color);
}

a:hover {
    color: var(--link-hover-color);
}

/* Buttons */
.btn-primary {
    background-color: var(--button-primary-color);
    border-color: var(--button-primary-color);
}

.btn-primary:hover {
    background-color: var(--button-primary-hover-color);
    border-color: var(--button-primary-hover-color);
}

.btn-secondary {
    background-color: var(--button-secondary-color);
    border-color: var(--button-secondary-color);
}

.btn-secondary:hover {
    background-color: var(--button-secondary-hover-color);
    border-color: var(--button-secondary-hover-color);
}

/* Cards */
.card {
    background-color: var(--card-background-color);
    border-color: var(--card-border-color);
}

/* Forms */
.form-control {
    border-color: var(--input-border-color);
    background-color: var(--background-color);
    color: var(--text-color);
}

.form-control:focus {
    border-color: var(--input-focus-color);
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Header */
.header {
    background-color: var(--header-background-color);
    color: var(--header-text-color);
}

/* Footer */
.footer {
    background-color: var(--footer-background-color);
    color: var(--footer-text-color);
}

/* Sidebar */
.sidebar {
    background-color: var(--sidebar-background-color);
    color: var(--sidebar-text-color);
}

/* Utility classes */
.text-primary { color: var(--primary-color) !important; }
.text-secondary { color: var(--secondary-color) !important; }
.text-success { color: var(--success-color) !important; }
.text-danger { color: var(--danger-color) !important; }
.text-warning { color: var(--warning-color) !important; }
.text-info { color: var(--info-color) !important; }
.text-light { color: var(--light-color) !important; }
.text-dark { color: var(--dark-color) !important; }

.bg-primary { background-color: var(--primary-color) !important; }
.bg-secondary { background-color: var(--secondary-color) !important; }
.bg-success { background-color: var(--success-color) !important; }
.bg-danger { background-color: var(--danger-color) !important; }
.bg-warning { background-color: var(--warning-color) !important; }
.bg-info { background-color: var(--info-color) !important; }
.bg-light { background-color: var(--light-color) !important; }
.bg-dark { background-color: var(--dark-color) !important; }

/* Border utilities */
.border-primary { border-color: var(--primary-color) !important; }
.border-secondary { border-color: var(--secondary-color) !important; }
.border-success { border-color: var(--success-color) !important; }
.border-danger { border-color: var(--danger-color) !important; }
.border-warning { border-color: var(--warning-color) !important; }
.border-info { border-color: var(--info-color) !important; }

/* Shadow utilities */
.shadow {
    box-shadow: 0 0.125rem 0.25rem var(--shadow-color) !important;
}

.shadow-sm {
    box-shadow: 0 0.125rem 0.25rem var(--shadow-color) !important;
}

.shadow-lg {
    box-shadow: 0 1rem 3rem var(--shadow-color) !important;
}

/* Gradient utilities */
.gradient-primary {
    background: linear-gradient(45deg, var(--gradient-start-color), var(--gradient-end-color));
}

/* Alert components */
.alert-primary {
    color: var(--primary-color);
    background-color: rgba(0, 123, 255, 0.1);
    border-color: var(--primary-color);
}

.alert-secondary {
    color: var(--secondary-color);
    background-color: rgba(108, 117, 125, 0.1);
    border-color: var(--secondary-color);
}

.alert-success {
    color: var(--success-color);
    background-color: rgba(40, 167, 69, 0.1);
    border-color: var(--success-color);
}

.alert-danger {
    color: var(--danger-color);
    background-color: rgba(220, 53, 69, 0.1);
    border-color: var(--danger-color);
}

.alert-warning {
    color: var(--warning-color);
    background-color: rgba(255, 193, 7, 0.1);
    border-color: var(--warning-color);
}

.alert-info {
    color: var(--info-color);
    background-color: rgba(23, 162, 184, 0.1);
    border-color: var(--info-color);
}`;

    return baseCSS;
};

/**
 * Apply colors to the current page dynamically
 * @param {Object} colors - Color settings object
 */
export const applyColorsToPage = (colors) => {
    const root = document.documentElement;
    
    // Apply all color variables
    Object.entries(colors).forEach(([key, value]) => {
        const cssVarName = `--${key.replace(/_/g, '-')}`;
        root.style.setProperty(cssVarName, value);
    });
    
    // Set legacy variables for backward compatibility
    root.style.setProperty('--themecolor', colors.primary_color || '#007bff');
    root.style.setProperty('--secthemecolor', colors.secondary_color || '#6c757d');
};

/**
 * Download CSS file with current color settings
 * @param {Object} colors - Color settings object
 * @param {string} filename - Filename for the CSS file
 */
export const downloadColorCSS = (colors, filename = 'custom-colors.css') => {
    const cssContent = generateCompleteCSS(colors);
    const blob = new Blob([cssContent], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
};

/**
 * Get color contrast ratio between two colors
 * @param {string} color1 - First color (hex)
 * @param {string} color2 - Second color (hex)
 * @returns {number} Contrast ratio
 */
export const getContrastRatio = (color1, color2) => {
    const getLuminance = (hex) => {
        const rgb = parseInt(hex.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >> 8) & 0xff;
        const b = (rgb >> 0) & 0xff;
        
        const [rs, gs, bs] = [r, g, b].map(c => {
            c = c / 255;
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        });
        
        return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    };
    
    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    
    return (brightest + 0.05) / (darkest + 0.05);
};

/**
 * Check if color combination meets accessibility standards
 * @param {string} textColor - Text color (hex)
 * @param {string} backgroundColor - Background color (hex)
 * @returns {Object} Accessibility check results
 */
export const checkAccessibility = (textColor, backgroundColor) => {
    const ratio = getContrastRatio(textColor, backgroundColor);
    
    return {
        ratio: ratio,
        aa: ratio >= 4.5, // WCAG AA standard
        aaa: ratio >= 7,  // WCAG AAA standard
        level: ratio >= 7 ? 'AAA' : ratio >= 4.5 ? 'AA' : 'Fail'
    };
};

/**
 * Generate color palette from a base color
 * @param {string} baseColor - Base color (hex)
 * @returns {Object} Color palette
 */
export const generateColorPalette = (baseColor) => {
    // This is a simplified palette generator
    // In a real implementation, you might want to use a more sophisticated algorithm
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    return {
        base: baseColor,
        light: `#${Math.min(255, Math.floor(r * 1.2)).toString(16).padStart(2, '0')}${Math.min(255, Math.floor(g * 1.2)).toString(16).padStart(2, '0')}${Math.min(255, Math.floor(b * 1.2)).toString(16).padStart(2, '0')}`,
        dark: `#${Math.max(0, Math.floor(r * 0.8)).toString(16).padStart(2, '0')}${Math.max(0, Math.floor(g * 0.8)).toString(16).padStart(2, '0')}${Math.max(0, Math.floor(b * 0.8)).toString(16).padStart(2, '0')}`,
        hover: `#${Math.max(0, Math.floor(r * 0.9)).toString(16).padStart(2, '0')}${Math.max(0, Math.floor(g * 0.9)).toString(16).padStart(2, '0')}${Math.max(0, Math.floor(b * 0.9)).toString(16).padStart(2, '0')}`
    };
};
